import Vue from "vue";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

import { DIContainer } from "@/app.container";
import { Dictionary } from "@/lib/Dictionary.type";
import dataStore from "@/store";

import FactoryRushCapacityMode from "../factory-rush-capacity-mode.model";
import FactoryRushCapacitySlot from "../factory-rush-capacity-slot.model";
import FactoryRushCapacityType from "../factory-rush-capacity-type.model";

const name = "FactoryRushCapacityStore";
const stateModules = (dataStore.state as unknown) as Record<string, unknown>;

if (stateModules[name]) {
  dataStore.unregisterModule(name);
}

const getDefaultState = () => {
  return {
    factoryRushCapacityMode: {},
    factoryRushCapacityType: {},
    factoryRushCapacitySlot: {},
    factorySlots: {},
  };
};

function createSlotKey(productId: string, typeId: string): string {
  return `${productId}:${typeId}`;
}

@Module({ name, dynamic: true, store: dataStore })
export default class FactoryRushCapacityStore extends VuexModule {
  factoryRushCapacityMode: Dictionary<boolean> = {};
  factoryRushCapacityType: Dictionary<FactoryRushCapacityType> = {};
  factoryRushCapacitySlot: Dictionary<FactoryRushCapacitySlot> = {};
  factorySlots: Dictionary<string[]> = {};

  get factoryRushCapacityTypesList(): FactoryRushCapacityType[] {
    return Object.values(this.factoryRushCapacityType);
  }

  get getFactoryRushCapacityModeById(): (id: string) => boolean {
    return (id: string) => !!this.factoryRushCapacityMode[id];
  }

  get getFactoryRushCapacitySlotsByFactoryId(): (
    factoryId: string
  ) => FactoryRushCapacitySlot[] {
    return (factoryId: string) => {
      const ids = this.factorySlots[factoryId];

      if (!ids) {
        return [];
      }

      return ids.map((id) => this.factoryRushCapacitySlot[id]);
    };
  }

  get getFactoryRushCapacitySlotByFactoryProductAndType(): (
    factoryId: string,
    productId: string,
    typeId: string
  ) => FactoryRushCapacitySlot | undefined {
    return (factoryId: string, productId: string, typeId: string) => {
      const ids = this.factorySlots[factoryId];

      if (!ids) {
        return undefined;
      }

      for (const id of ids) {
        const slot = this.factoryRushCapacitySlot[id];

        if (slot.product === productId && slot.type === typeId) {
          return slot;
        }
      }

      return undefined;
    };
  }

  @Mutation
  updateFactoryRushCapacityMode({
    id,
    value,
  }: {
    id: string;
    value: boolean;
  }): void {
    Vue.set(this.factoryRushCapacityMode, id, value);
  }

  @Mutation
  updateFactoryRushCapacityType(payload: FactoryRushCapacityType): void {
    Vue.set(this.factoryRushCapacityType, payload.id, payload);
  }

  @Mutation
  updateFactoryRushCapacitySlots({
    factoryId,
    slots,
  }: {
    factoryId: string;
    slots: FactoryRushCapacitySlot[];
  }): void {
    const previousIds = this.factorySlots[factoryId] || [];
    const slotIds: string[] = [];
    const nextIds: Dictionary<boolean> = {};

    for (const slot of slots) {
      if (slot.factory !== factoryId) {
        throw new Error(
          "All rush capacity slots should belong to the specified factory"
        );
      }

      slotIds.push(slot.id);
      nextIds[slot.id] = true;
      Vue.set(this.factoryRushCapacitySlot, slot.id, slot);
    }

    for (const id of previousIds) {
      if (nextIds[id]) {
        continue;
      }

      Vue.delete(this.factoryRushCapacitySlot, id);
    }

    Vue.set(this.factorySlots, factoryId, slotIds);
  }

  @Action({ rawError: true })
  async enableFactoryRushCapacityModeById(id: string): Promise<void> {
    const mode = new FactoryRushCapacityMode(id);

    await DIContainer.FactoryRushCapacityModeRepository.save(mode);

    this.updateFactoryRushCapacityMode({ id, value: true });
  }

  @Action({ rawError: true })
  async disableFactoryRushCapacityModeById(id: string): Promise<void> {
    await DIContainer.FactoryRushCapacityModeRepository.deleteById(id);

    this.updateFactoryRushCapacityMode({ id, value: false });
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacityModeById({
    id,
    useCache = true,
  }: {
    id: string;
    useCache?: boolean;
  }): Promise<boolean> {
    if (useCache && id in this.factoryRushCapacityMode) {
      return !!this.factoryRushCapacityMode[id];
    }

    const items = await DIContainer.FactoryRushCapacityModeRepository.getByIds([
      id,
    ]);

    const isEnabled = items[id] !== undefined;

    this.updateFactoryRushCapacityMode({ id, value: isEnabled });

    return isEnabled;
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacityModesByIds({
    ids,
    useCache = true,
  }: {
    ids: string[];
    useCache?: boolean;
  }): Promise<Dictionary<boolean>> {
    const missing: string[] = [];
    const result: Dictionary<boolean> = {};

    ids.forEach((id) => {
      if (useCache && id in this.factoryRushCapacityMode) {
        result[id] = !!this.factoryRushCapacityMode[id];
        return;
      }

      missing.push(id);
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.FactoryRushCapacityModeRepository.getByIds(
      missing
    );

    missing.forEach((id) => {
      const isEnabled = items[id] !== undefined;

      this.updateFactoryRushCapacityMode({ id, value: isEnabled });
      result[id] = isEnabled;
    });

    return { ...result };
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacityTypes({
    useCache = true,
  }: {
    useCache?: boolean;
  } = {}): Promise<FactoryRushCapacityType[]> {
    if (!useCache || !this.factoryRushCapacityTypesList.length) {
      const collection = await DIContainer.FactoryRushCapacityTypeRepository.getList(
        1,
        999
      );

      collection.getItems().forEach((item) => {
        this.updateFactoryRushCapacityType(item);
      });
    }

    return this.factoryRushCapacityTypesList;
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacitySlotsByFactoryId({
    factoryId,
    useCache = true,
  }: {
    factoryId: string;
    useCache?: boolean;
  }): Promise<FactoryRushCapacitySlot[]> {
    if (useCache && this.factorySlots[factoryId]) {
      return this.getFactoryRushCapacitySlotsByFactoryId(factoryId);
    }

    const collection = await DIContainer.FactoryRushCapacitySlotRepository.getByFactoryId(
      factoryId,
      useCache
    );

    const items = collection.getItems();

    this.updateFactoryRushCapacitySlots({ factoryId, slots: items });

    return items;
  }

  @Action({ rawError: true })
  async saveFactoryRushCapacitySlotsByFactoryId({
    factoryId,
    slots,
  }: {
    factoryId: string;
    slots: FactoryRushCapacitySlot[];
  }): Promise<FactoryRushCapacitySlot[]> {
    const currentSlots = await this.loadFactoryRushCapacitySlotsByFactoryId({
      factoryId,
    });

    const currentSlotsMap: Dictionary<FactoryRushCapacitySlot> = {};
    const desiredSlotsMap: Dictionary<FactoryRushCapacitySlot> = {};

    currentSlots.forEach((slot) => {
      currentSlotsMap[createSlotKey(slot.product, slot.type)] = slot;
    });

    slots.forEach((slot) => {
      desiredSlotsMap[createSlotKey(slot.product, slot.type)] = slot;
    });

    const savePromises: Promise<FactoryRushCapacitySlot>[] = [];
    const deletePromises: Promise<void>[] = [];

    Object.keys(desiredSlotsMap).forEach((key) => {
      const desiredSlot = desiredSlotsMap[key];
      const currentSlot = currentSlotsMap[key];

      if (!currentSlot) {
        desiredSlot.isNew = true;
        savePromises.push(
          DIContainer.FactoryRushCapacitySlotRepository.save(desiredSlot)
        );
        return;
      }

      if (currentSlot.capacity === desiredSlot.capacity) {
        return;
      }

      desiredSlot.isNew = false;
      savePromises.push(
        DIContainer.FactoryRushCapacitySlotRepository.save(desiredSlot)
      );
    });

    Object.keys(currentSlotsMap).forEach((key) => {
      if (desiredSlotsMap[key]) {
        return;
      }

      deletePromises.push(
        DIContainer.FactoryRushCapacitySlotRepository.delete(
          currentSlotsMap[key]
        )
      );
    });

    const requests: Array<Promise<unknown>> = [];

    savePromises.forEach((request) => {
      requests.push(request);
    });

    deletePromises.forEach((request) => {
      requests.push(request);
    });

    await Promise.all(requests);

    return this.loadFactoryRushCapacitySlotsByFactoryId({
      factoryId,
      useCache: false,
    });
  }

  @Mutation
  resetState(): void {
    const state = stateModules[name] as Record<string, unknown> | undefined;

    if (state) {
      Object.assign(state, getDefaultState());
    }
  }
}
